import { injectable, inject } from 'inversify';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

import { t } from '@vk-hr-tek/core/translations';
import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';
import { RoleService } from '@vk-hr-tek/core/http';
import { ChipColorUnion } from '@vk-hr-tek/ui/ColoredChip/ColoredChip.types';

import {
  Event as EventDetailEntity,
  NodeAction,
  Attribute,
  FormAttribute,
  VacationPeriodsValidator,
  EventListItem as EventListEntity,
  ImportedAbsenceItem,
} from '@app/gen/events';

import {
  EventWithValidators,
  isTextAttribute,
  isMultipleDocumentTextAttribute,
  VacationsRangeType,
  Validator,
  ImportedAbsence,
} from '../../types';
import {
  VACATION_START_ATTR_ID,
  VACATION_END_ATTR_ID,
  VACATION_TYPE_ATTR_ID,
  ALLOW_COMPENSATION_ATTR_ID,
} from '../../constants';
import { VacationsRangesCachedDto } from '../../dto';

const signStatuses: Record<string, string> = {
  signed: 'Подписано',
  on_signing: 'На подписании',
  can_sign: 'Подписать',
  not_signed: 'Не подписано',
};

interface RandgesInitialValues {
  ranges: VacationsRangeType[] | [];
  comment?: string;
}

@injectable()
export class VacationDetailMapper {
  constructor(
    private format: FormatService,
    private calendar: Calendar,
    @inject(RoleService) private roleService: RoleService,
  ) {}

  private processTags(event: EventWithValidators) {
    const myNodeActions = event.nodes.map(({ actions }) => actions).flat();

    return event.active_nodes.map(
      ({ action: { name, type }, document_type_names, responsible }) => ({
        items: [
          responsible !== t('vacations.detail.mapper.employeeText') &&
          name === 'Проверка'
            ? 'Согласование'
            : name,
          responsible || '',
          ...(document_type_names || []),
        ],
        isActive: myNodeActions.some(
          (action) =>
            action.type === type &&
            action.responsible.some((resp) => resp.role === responsible),
        ),
      }),
    );
  }

  processDetailEmployee(event: EventListEntity | EventDetailEntity) {
    if (event.employee) {
      const units: {
        label: string;
        mobileLabel: string;
        value: string;
        color: ChipColorUnion;
        width: '50%' | '100%';
      }[] = [];

      if (event.employee.unit) {
        units.push({
          label: 'Управленческая',
          mobileLabel: 'Упр. структура',
          value: event.employee.unit,
          color: 'blue',
          width: '50%',
        });
      }

      if (event.employee.legal_unit) {
        units.push({
          label: 'Юридическая',
          mobileLabel: 'Юр. структура',
          value: event.employee.legal_unit,
          color: 'purple',
          width: '50%',
        });
      }

      if (units.length === 1) {
        units[0].width = '100%';
      }

      return {
        id: event.employee.id,
        name: `${event.employee.second_name} ${event.employee.first_name}`,
        fullName: `${event.employee.second_name} ${event.employee.first_name} ${event.employee.middle_name}`,
        fio: `${event.employee.second_name} ${event.employee.first_name[0]}.${event.employee.middle_name[0]}.`,
        avatar: `${event.employee.second_name[0]}${event.employee.first_name[0]}`,
        personnelNumber: event.employee.personnel_number,
        birthday: event.employee.birthday,
        dismissed:
          !!event.employee.dismissed_at &&
          Date.now() > +new Date(event.employee.dismissed_at),
        dismissedAt: event.employee.dismissed_at
          ? this.format.toDate(event.employee.dismissed_at)
          : null,
        phone: event.employee.phone ?? '-',
        email: event.employee.email ?? '-',
        position: event.employee.position,
        units,
      };
    }
  }

  processImportedAbsences(items?: ImportedAbsenceItem[]): ImportedAbsence[] {
    return (items ?? []).map((item) => ({
      typeName: item.type_name,
      startDate: this.calendar.startOfDay(new Date(item.from_date)),
      endDate: this.calendar.startOfDay(new Date(item.to_date)),
      intersectionsCount: item.intersections_count,
    }));
  }

  processVacation(event: EventListEntity | EventDetailEntity) {
    const start = event.attributes.find(
      (attr) => attr.id === VACATION_START_ATTR_ID,
    );
    const end = event.attributes.find(
      (attr) => attr.id === VACATION_END_ATTR_ID,
    );

    const startDate = start ? new Date(start.value as string) : null;
    const endDate = end ? new Date(end.value as string) : null;
    let count: string | number = '';

    if (startDate && endDate) {
      count =
        startDate <= endDate
          ? this.calendar.countOfVacationDays(endDate, startDate)
          : 'Ошибка';
    }

    return {
      start: start ? this.format.toDate(start.value as string) : '',
      end: end ? this.format.toDate(end.value as string) : '',
      count,
      isHolidaysInVacationInterval: Boolean(
        startDate &&
        endDate &&
        startDate <= endDate &&
        this.calendar.countOfHolidaysInInterval(startDate, endDate),
      ),
    };
  }

  processValidator(validators: Validator[], skipDefault = false) {
    return [
      ...uniq(
        validators.map((validator) => {
          if (validator.type === 'min_duration' && validator.days_count) {
            return `Минимальная длительность отпуска — ${this.format.pluralize(
              Math.abs(validator.days_count),
              [
                validator.working_days_only
                  ? 'рабочий день'
                  : 'календарный день',
                validator.working_days_only ? 'рабочих дня' : 'календарных дня',
                validator.working_days_only
                  ? 'рабочих дней'
                  : 'календарных дней',
              ],
            )}`;
          }

          if (validator.type === 'max_duration' && validator.days_count) {
            return `Максимальная длительность отпуска — ${this.format.pluralize(
              Math.abs(validator.days_count),
              [
                validator.working_days_only
                  ? 'рабочий день'
                  : 'календарный день',
                validator.working_days_only ? 'рабочих дня' : 'календарных дня',
                validator.working_days_only
                  ? 'рабочих дней'
                  : 'календарных дней',
              ],
            )}`;
          }

          if (validator.type === 'divisible_by') {
            return `Количество дней в отпуске должно быть кратно ${validator.divisible_by}`;
          }

          if (validator.type === 'vacation_schedule_14_days') {
            return 'Хотя бы один отпуск должен быть не менее 14 календарных дней';
          }

          if (validator.type === 'vacation_limit_to_calculated_days') {
            return 'Можно запланировать только расчётное количество дней';
          }

          if (validator.type === 'vacation_schedule_all_days') {
            return 'Нужно запланировать все дни';
          }

          if (validator.type === 'weekend_exclusion') {
            return t(
              'vacations.detail.mapper.cannotTakeTwoSingleDayLeavesLabel',
            );
          }

          if (validator.type === 'min_specific_weekend_days') {
            return 'Отпуск должен включать определенное (зависит от длины отпуска) количество выходных дней';
          }

          if (validator.type === 'vacation_min_5_days_between_not_workdays') {
            return t(
              'vacations.detail.mapper.cannotTakeLeaveLessThan5DaysAdjacentLabel',
            );
          }

          if (validator.type === 'working_day') {
            return 'В отпуске должен быть хотя бы один рабочий день';
          }

          return '';
        }),
      ),
      ...(skipDefault ? [] : ['Отпуска не могут пересекаться']),
    ].filter((text) => text);
  }

  processDetailEntity(
    event: EventWithValidators,
    cache: Partial<VacationsRangesCachedDto> | null,
  ) {
    const role = this.roleService.get();

    return {
      ...event,
      id: event.id,
      title: event.event_type.name,
      tags: this.processTags(event),
      company: event.company.name,
      createdAt: this.format.toDate(event.created_at),
      consecutiveNumber: event.consecutive_number,
      employee: this.processDetailEmployee(event),
      documents: event.documents.map((doc) => {
        return {
          ...doc,
          signatures: doc.signatures.map(
            ({ status, signed_by_name, signed_by }) => ({
              status: signStatuses[status] || '',
              signed_by_name: signed_by_name || '',
              signed_by: signed_by || '',
            }),
          ),
        };
      }),
      attributes: event.attributes.map((item) => ({
        ...item,
        value:
          item.type === 'date'
            ? this.format.toDate(item.value as string)
            : item.value,
      })),
      visibleAttributes: !event.nodes?.length
        ? {
            initialValues: this.processActionInitialValues(
              event.attributes,
              [],
            ),
            validators: [],
            vacationsAvailable: {},
            form_attributes: [],
            formAttributes: [],
            validationRules: [],
            name: '',
          }
        : null,
      nodeActions:
        event.nodes &&
        event.nodes.length &&
        event.nodes.map((node) => ({
          ...node,
          deadline: node.deadline ? this.processDeadline(node.deadline) : '',
          actions: node.actions
            .filter(({ type }) =>
              [
                'year',
                'upload',
                'upload_batch',
                'generate_document_from_template',
                'accept',
                'unep_sign',
                'ukep_sign',
                'pep_sign',
                'decline',
                'decline_sign',
                'declined',
                'declined_sign',
                'unep_sign_batch',
                'ukep_sign_batch',
                'pep_sign_batch',
                'return',
              ].includes(type),
            )
            .map((action) => {
              const validators =
                event.validators && event.validators[node.node_id]
                  ? event.validators[node.node_id]
                  : [];

              const formAttributes = this.processFormAttributes(
                action,
                event.company.name,
              );

              const generateDocumentFromTemplateNodes =
                event?.active_nodes?.filter(
                  (activeNodes) =>
                    activeNodes.action.type ===
                    'generate_document_from_template',
                );

              const documentName =
                (generateDocumentFromTemplateNodes.length === 1 &&
                  generateDocumentFromTemplateNodes[0]
                    .document_type_names?.[0]) ||
                '';

              const vacationsAvailable: Record<string, number> =
                (validators.find(
                  (
                    validator: Validator,
                  ): validator is VacationPeriodsValidator =>
                    validator.type === 'vacation_periods',
                )?.vacations as Record<string, number>) || {};

              const initialValues = this.processActionInitialValues(
                event.attributes,
                action.form_attributes,
              );

              if (
                role === 'employee' &&
                action.form_attributes.length &&
                !initialValues.ranges.length &&
                cache
              ) {
                initialValues.comment = cache.comment || initialValues.comment;
                initialValues.ranges =
                  cache.ranges?.map((range) => ({
                    ...range,
                    startVacation: this.calendar.startOfDay(
                      new Date(range.startVacation),
                    ),
                    endVacation: this.calendar.startOfDay(
                      new Date(range.endVacation),
                    ),
                    compensation: false,
                  })) || initialValues.ranges;
              }

              const choiceFormAttribute = action?.form_attributes.find(
                ({ type, id }) =>
                  type === 'choice' && id === VACATION_TYPE_ATTR_ID,
              );

              const validatorsByVacationTypes = validators.reduce(
                (acc, validator) => {
                  if (
                    'by_vacation_type' in validator &&
                    validator.by_vacation_type
                  ) {
                    const result = { ...acc };

                    Object.keys(validator.by_vacation_type).forEach((key) => {
                      const lbl =
                        choiceFormAttribute?.options?.choices?.find(
                          (choice) => choice.value === key,
                        )?.label || key;

                      result[lbl] = [
                        ...(result[lbl] || []),
                        ...this.processValidator(
                          [
                            {
                              ...validator,
                              ...(
                                validator?.by_vacation_type as Record<
                                  string,
                                  Validator
                                >
                              )[key],
                            },
                          ],
                          true,
                        ),
                      ];
                    });

                    return result;
                  }

                  if (
                    'vacation_types' in validator &&
                    validator.vacation_types?.length
                  ) {
                    const result = { ...acc };

                    validator.vacation_types.forEach((key) => {
                      const lbl =
                        choiceFormAttribute?.options?.choices?.find(
                          (choice) => choice.value === key,
                        )?.label || key;

                      result[lbl] = [
                        ...(result[lbl] || []),
                        ...this.processValidator([validator], true),
                      ];
                    });

                    return result;
                  }

                  return acc;
                },
                {} as Record<string, string[]>,
              );

              const validationRules = {
                general: this.processValidator(
                  validators.filter((validator) => {
                    return !(
                      'vacation_types' in validator &&
                      validator.vacation_types?.length
                    );
                  }),
                ),
                ...validatorsByVacationTypes,
              };

              const showValidationRules = Object.keys(validationRules).some(
                (rule) =>
                  (validationRules as Record<string, string[]>)[rule]?.length,
              );

              return {
                ...action,
                vacationsAvailable,
                validationRules,
                showValidationRules,
                validators: validators.map((validator) => {
                  return {
                    ...validator,
                    to_attribute_id: 'id_end',
                    from_attribute_id: 'id_start',
                  };
                }),
                initialValues,
                documentName,
                form_attributes: formAttributes,
                formAttributes,
              };
            }),
        })),
      current_nodes: event.current_nodes.map((node) => ({
        ...node,
        deadline: node.deadline ? this.processDeadline(node.deadline) : '',
      })),
      deadline: event.deadline ? this.format.toDate(event.deadline) : '',
      cancelActionInfo:
        event.canceled &&
        !event.canceled.reason
          .toLowerCase()
          .includes(t('vacations.detail.mapper.deadlineText'))
          ? {
              reason: event.canceled.reason,
              comment: event.canceled.comment,
            }
          : null,
      info: this.processDetailInfo(event),
      actions: this.processDetailActions(event),
      vacation: this.processVacation(event),
      returns: this.processComments(event),
      returnNodeId:
        !!event.nodes &&
        !!event.nodes.length &&
        event.nodes.find(({ actions: nodeAction }) =>
          nodeAction.some(({ type }) => type === 'return'),
        )?.node_id,
      signedAfterCancel: event.signed_after_cancel,
      options: {
        availableVacationDays: event?.options?.available_vacation_days,
        availableVacationDaysByTypes:
          event?.options?.available_vacation_days_by_types,
      },
    };
  }

  processActionInitialValues = (
    attributes: Attribute[],
    formAttributes: FormAttribute[],
  ): RandgesInitialValues => {
    const start = attributes.find((attr) => attr.id === VACATION_START_ATTR_ID);
    const end = attributes.find((attr) => attr.id === VACATION_END_ATTR_ID);
    const types = attributes.find(
      (attr) => attr.type === 'choice' && attr.id === VACATION_TYPE_ATTR_ID,
    );
    const comment = attributes.find((attr) => attr.type === 'textarea');
    const compensations = attributes.find(
      (attr) =>
        attr.type === 'choice' && attr.id === ALLOW_COMPENSATION_ATTR_ID,
    );

    const typesAttributeChoices =
      formAttributes.find((attr) => attr.type === 'choice')?.options?.choices ||
      [];
    const typesValue: Record<string, string> = typesAttributeChoices.reduce(
      (acc, attr) => {
        return {
          ...acc,
          [attr.label]: attr.value,
        };
      },
      {} as Record<string, string>,
    );

    const startsVacations =
      start?.value &&
      Array.isArray(start.value) &&
      isMultipleDocumentTextAttribute(start)
        ? start.value
        : [];
    const endsVacations =
      end?.value &&
      Array.isArray(end.value) &&
      isMultipleDocumentTextAttribute(end)
        ? end.value
        : [];
    const typesVacations =
      types?.value && isMultipleDocumentTextAttribute(types) ? types.value : [];
    const compensationsVacations =
      compensations?.value && isMultipleDocumentTextAttribute(compensations)
        ? compensations.value
        : [];

    const ranges = startsVacations?.map((item, index) => {
      return {
        startVacation: this.calendar.startOfDay(new Date(item)),
        endVacation: this.calendar.startOfDay(new Date(endsVacations[index])),
        type: Object.keys(typesValue).length
          ? typesValue[typesVacations[index]]
          : typesVacations[index],
        compensation: compensationsVacations[index] === 'true',
      };
    });

    return {
      ranges: ranges || [],
      comment: comment && isTextAttribute(comment) ? comment.value : '',
    };
  };

  processDetailActions(event: EventDetailEntity) {
    const actions: (
      | 'to_paper'
      | 'print'
      | 'download'
      | 'cancel'
      | 'decline'
      | 'declined'
    )[] = [];

    const eventDocumentsStatusesArr = [];

    for (const document of event.documents) {
      for (const signature of document.signatures) {
        eventDocumentsStatusesArr.push(signature.status);
      }
    }

    const documentsIsExists = event?.documents && event.documents.length > 0;
    const signedDocumentIsExists = eventDocumentsStatusesArr.includes('signed');

    if (event.permissions.to_paper) {
      actions.push('to_paper');
    }

    if (documentsIsExists && signedDocumentIsExists) {
      actions.push('download');
    }

    if (event.documents && event.documents.length) {
      actions.push('print');
    }

    if (
      !!event.active_nodes &&
      !!event.active_nodes.length &&
      event.permissions.cancel &&
      event.active_nodes.some(
        ({ action }) =>
          ![
            'completed',
            'system',
            'in_paper',
            'decline',
            'declined',
            'declined_sign',
          ].includes(action.type),
      )
    ) {
      actions.push('cancel');
    }

    return actions;
  }

  processDetailInfo(event: EventDetailEntity) {
    if (!event.canceled) {
      return '';
    } else if (event.canceled.reason_type.startsWith('manual')) {
      return `Отменено ${this.format.toDate(event.canceled.canceled_at)} ${
        event.canceled.reason_type === 'manual_company'
          ? 'представителем компании'
          : t('vacations.detail.mapper.cancelByyEmployeeText')
      }: ${event.canceled.responsible}`;
    } else if (
      event.canceled.reason_type === 'node_deadline' ||
      event.canceled.reason_type === 'event_deadline'
    ) {
      return `Отменено ${this.format.toDate(event.canceled.canceled_at)} ${
        event.canceled.reason
      }`;
    } else if (
      event.canceled.reason_type === 'workflow_change' &&
      event.canceled.comment
    ) {
      return `${event.canceled.comment}`;
    } else if (event.canceled.reason_type === 'dismiss') {
      return `${t(
        'vacations.flow.mapper.cancelEmployeeDismissalText',
      )}. Дата увольнения: ${this.format.toDate(
        event.employee?.dismissed_at || '',
      )}`;
    } else {
      return '';
    }
  }

  processComments(event: EventDetailEntity) {
    if (!event.comments) {
      return null;
    }

    const sortComments = orderBy(
      event.comments,
      [({ created_at }) => new Date(created_at).getTime()],
      ['desc'],
    );

    return sortComments.map(({ comment, user, created_at, role, type }) => ({
      createdId: `${created_at}_${user?.id || ''}_${comment.length}`,
      comment,
      role,
      createdAt: this.format.toDateTime(created_at),
      type: type || undefined,
      returnLabel:
        type === 'return'
          ? t('event.eventDetail.returnForRevisionComment')
          : '',
      user: {
        id: user?.id || '',
        middleName: user?.middle_name || '',
        firstName: user?.first_name || '',
        lastName: user?.last_name || '',
        avatar: `${user?.first_name[0] || ''}${
          user?.last_name[0] || ''
        }`.toLowerCase(),
      },
    }));
  }

  processFormAttributes(action: NodeAction, companyName: string) {
    return action.form_attributes.map((attribute) => {
      return {
        ...attribute,
        companyName,
      };
    });
  }

  processDeadline(deadline: string) {
    const durationDate = this.calendar.formatISO(
      this.calendar.subMinutes(new Date(deadline), 1),
    );

    return `${this.format.toTime(durationDate)} ${this.format.toDate(
      durationDate,
    )}`;
  }
}
